import { Component, HostListener } from '@angular/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent {
  title = 'Central Plant Manager';
  // Shared message type used by the host portal and iframe apps for shortcut relays.
  private readonly keyboardShortcutMessageType = '75F_KEYBOARD_SHORTCUT';
  // Prevents a shortcut replayed from the parent from being immediately posted back to the parent.
  private suppressNextParentShortcutRelay = false;
  chatSessionActive: boolean = false;
  isPublicUrl: boolean = true;
  chatEnabled: boolean = false;
  /**
   * Tracks whether keyboard tab navigation focus is active in the parent shell (topbar/sidebar).
   * Used to know whether a click inside this iframe needs to clear the parent outline.
   */
  isTabNavActive: boolean = false;
  constructor() {
    const currentUrl = new URL(window.location.href);
    const { searchParams, pathname } = currentUrl;
    const rawChatEnabled = (searchParams.get('chatEnabled') || '').toLowerCase();
    this.chatEnabled = rawChatEnabled === 'true' || rawChatEnabled === '1';
    console.log(this.chatEnabled,'chatEnabled')
    const pathParts = pathname.split('/').filter(Boolean);
    this.isPublicUrl = pathParts[2] === 'public';
  }

    //Change triggered from parent componet(Internal/Facilisight)
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    let messageFromParent: any = event?.data;
    if (typeof event?.data === 'string') {
      try {
        messageFromParent = JSON.parse(event.data);
      } catch {
        return;
      }
    }

    //Handle the chatSessionActive state(we are swtiching the icons absed on this)
    if (messageFromParent?.chatSessionActive !== undefined) {
      this.chatSessionActive = messageFromParent.chatSessionActive;
    }

    // Synchronize tab navigation state from parent shell (indicates whether topbar/sidebar outline is visible)
    if (messageFromParent?.type === '75F_TAB_NAV_STATE') {
      this.isTabNavActive = !!messageFromParent.active;
    }

    // Parent focus -> iframe shortcut: replay parent-owned messages as local keydown events.
    if (messageFromParent?.type === this.keyboardShortcutMessageType && messageFromParent?.source === 'PARENT') {
      this.replayShortcutFromParent(messageFromParent);
    }
  }

  //The icon is 
  onToggleChat() {
    window.parent.postMessage(JSON.stringify({chatSessionActive:this.chatSessionActive}), '*');
  }

  /**
   * Intercepts keyboard navigation and shortcut events within the iframe and forwards them to the parent shell.
   * - Tab / Shift+Tab: Allows keyboard focus to cycle between the embedded iframe and the host shell (topbar/sidebar).
   * - Escape: Closes active host panels/drawers or clears focus.
   * - ? / Shift+/: Opens the keyboard shortcut help dialog in the host portal.
   * - S: Opens Saffron AI in the host portal.
   *
   * Form inputs, textareas, and contenteditable elements are bypassed to allow normal typing.
   */
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Do not echo the synthetic keydown that was created from a parent shortcut message.
    if (this.suppressNextParentShortcutRelay) {
      this.suppressNextParentShortcutRelay = false;
      return;
    }

    const target = event.target as HTMLElement;
    const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

    // Do not intercept if user is typing inside a text box or form input
    if (isInput) {
    
      return;
    }

    const isTab = event.key === 'Tab' || event.code === 'Tab';
    const isEscape = event.key === 'Escape' || event.code === 'Escape';
    const isHelp = event.key === '?' || (event.shiftKey && (event.key === '/' || event.code === 'Slash'));
    const isChatShortcut = event.key.toLowerCase() === 's' && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey;

    // Iframe focus -> parent shortcut: forward host-owned navigation/help shortcuts.
    if (isTab || isEscape || isHelp) {
      event.preventDefault();
      this.postShortcutToParent(event);
    } else if (isChatShortcut && this.chatEnabled && !this.chatSessionActive) {
      // Saffron/chat is owned by the host shell, so send the shortcut to parent instead of handling it here.
      event.preventDefault();
      this.postShortcutToParent(event);
    } else if (this.shouldBridgeKeyboardEvent(event)) {
      // Forward other app shortcut candidates; parent will ignore anything it does not own.
      this.postShortcutToParent(event);
    }
  }

  /**
   * Clears the tab navigation outline on the parent topbar/sidebar when the user clicks inside the iframe.
   * Only posts a message when `isTabNavActive` is true to avoid unnecessary postMessage traffic on every click.
   */
  @HostListener('window:mousedown')
  onMousedown(): void {
    if (this.isTabNavActive) {
      this.isTabNavActive = false;
      window.parent.postMessage(JSON.stringify({ type: '75F_IFRAME_MOUSEDOWN' }), '*');
    }
  }

  // Replays a parent shortcut inside CPM so existing local shortcut services can handle it normally.
  private replayShortcutFromParent(payload: any): void {
    this.suppressNextParentShortcutRelay = true;
    document.dispatchEvent(new KeyboardEvent('keydown', {
      key: payload.key,
      code: payload.code,
      shiftKey: !!payload.shiftKey,
      altKey: !!payload.altKey,
      ctrlKey: !!payload.ctrlKey,
      metaKey: !!payload.metaKey,
      bubbles: true,
      cancelable: true
    }));
  }

  // Sends an iframe keydown to the parent shell using the shared shortcut bridge message format.
  private postShortcutToParent(event: KeyboardEvent): void {
    if (window.parent === window) {
      return;
    }

    window.parent.postMessage(JSON.stringify({
      type: this.keyboardShortcutMessageType,
      key: event.key,
      code: event.code,
      shiftKey: !!event.shiftKey,
      altKey: !!event.altKey,
      ctrlKey: !!event.ctrlKey,
      metaKey: !!event.metaKey,
      plain: !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey,
      source: 'IFRAME',
      sequence: `IFRAME-${Date.now()}-${Math.random()}`
    }), '*');
  }

  // Keeps normal typing and browser-reserved shortcuts out of the iframe bridge.
  private shouldBridgeKeyboardEvent(event: KeyboardEvent): boolean {
    if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
      return false;
    }

    // Relay app shortcut candidates only: navigation keys plus plain letters/digits.
    return event.key === 'Escape'
      || event.key === 'Tab'
      || event.key === '?'
      || event.code === 'Slash'
      || event.code?.startsWith('Key')
      || event.code?.startsWith('Digit');
  }
}
